import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchAdminMe, fetchCampagnes, runProNudge, runEquipementsNudge } from "@/lib/api";
import { Megaphone, Loader2, ArrowLeft, Crown, Home, Play, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

export default function AdminCampagnesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [running, setRunning] = useState("");

  const load = useCallback(() => {
    fetchCampagnes().then(setData).catch(() => toast.error("Erreur de chargement"));
  }, []);

  useEffect(() => {
    fetchAdminMe().then(load).catch(() => navigate("/", { replace: true }));
  }, [navigate, load]);

  const run = async (kind) => {
    setRunning(kind);
    try {
      const res = kind === "pro" ? await runProNudge() : await runEquipementsNudge();
      toast.success(`Campagne lancee : ${res.nudged} vendeur(s) relance(s)`);
      load();
    } catch { toast.error("Erreur lors du lancement"); }
    setRunning("");
  };

  if (!data) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-20 text-center">
        <Loader2 className="animate-spin mx-auto text-orange-500" />
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-campagnes-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-orange-600 font-semibold mb-4" data-testid="back-to-admin">
        <ArrowLeft size={14} /> Tableau de bord
      </Link>
      <h1 className="font-display text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
        <Megaphone size={32} className="text-orange-500" /> Campagnes automatiques
      </h1>
      <p className="text-[var(--ink-soft)] mt-2">
        Relances email/SMS envoyees automatiquement chaque jour a vos vendeurs. Lancez un passage manuel a tout moment.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <CampagneCard
          icon={Crown}
          color="from-amber-500 to-yellow-400"
          title="Relance Fodindin Pro"
          desc="Vendeurs actifs non-Pro dont les annonces ont du succes — invitation a passer Pro (2000 FCFA/mois)."
          stats={data.pro}
          onRun={() => run("pro")}
          running={running === "pro"}
          testid="campagne-pro"
        />
        <CampagneCard
          icon={Home}
          color="from-emerald-500 to-teal-400"
          title="Relance Equipements"
          desc="Vendeurs immobilier sans equipements renseignes — invitation a completer pour la recherche filtree."
          stats={data.equipements}
          onRun={() => run("equipements")}
          running={running === "equipements"}
          testid="campagne-equipements"
        />
      </div>
    </main>
  );
}

function CampagneCard({ icon: Icon, color, title, desc, stats, onRun, running, testid }) {
  return (
    <section className="glass-card p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${color} text-white grid place-items-center shrink-0`}>
            <Icon size={20} />
          </div>
          <div>
            <h2 className="font-bold text-lg">{title}</h2>
            <p className="text-xs text-[var(--ink-soft)]">{desc}</p>
          </div>
        </div>
        <button
          onClick={onRun}
          disabled={running}
          className="btn-primary !py-2 !px-3 text-xs inline-flex items-center gap-1 shrink-0"
          data-testid={`${testid}-run`}
        >
          {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Lancer
        </button>
      </div>

      <div className="flex gap-6 mt-5">
        <div>
          <div className="text-2xl font-extrabold" data-testid={`${testid}-total`}>{stats.total}</div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)]">Relances envoyees</div>
        </div>
        <div>
          <div className="text-2xl font-extrabold">{stats.last_run ? stats.last_run.slice(0, 10) : "—"}</div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)]">Derniere relance</div>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-xs font-bold uppercase tracking-wide text-[var(--ink-soft)] mb-2">Dernieres relances</div>
        {stats.recent.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)]">Aucune relance envoyee pour le moment.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats.recent.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl border border-[var(--line)] text-sm" data-testid={`${testid}-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.user_name || r.user_email || r.user_phone || r.user_id}</div>
                  <div className="text-[10px] text-[var(--ink-soft)]">
                    {(r.created_at || "").slice(0, 16).replace("T", " ")}
                    {r.views != null && ` · ${r.views} vues`}
                    {r.annonces_count != null && ` · ${r.annonces_count} annonce(s)`}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {(r.channels || []).includes("email") && <span className="w-6 h-6 rounded-full bg-sky-50 text-sky-600 grid place-items-center" title="Email"><Mail size={12} /></span>}
                  {(r.channels || []).includes("sms") && <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center" title="SMS"><Phone size={12} /></span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
